import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { UserService } from '@app/user/user.service';
import { NotificationsService } from '@shared/notifications.service';
import { InteractionService } from '@app/interaction/interaction.service';
import { ConfirmStatus } from '@app/interaction/confirm/confirm.component';

@Component({
    selector: 'app-user-notes',
    templateUrl: './user-notes.component.html',
    styleUrls: ['./user-notes.component.scss'],
    standalone: false
})

export class UserNotesComponent implements OnInit, OnChanges {
    @Input() userId?: number;
    @Input() notes?: any[]; // Optional notes list (if provided, won't fetch)
    @Input() maxNotes: number = 3; // How many latest notes to display
    
    displayedNotes: any[] = [];
    isLoading = false;
    
    // Note modal state
    showNoteModal: boolean = false;
    editedNote: string = '';
    isClosingNoteModal: boolean = false;
    showNoteError: boolean = false;
    isEditingNote: boolean = false;
    editingNoteId: number | null = null;

    ngOnInit(): void {
        this.updateDisplayedNotes();
        if (!this.notes && this.userId) {
            this.loadNotes();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['notes'] || changes['maxNotes']) {
            this.updateDisplayedNotes();
        }
        if (changes['userId'] && this.userId && !this.notes) {
            this.loadNotes();
        }
    }

    updateDisplayedNotes(): void {
        if (this.notes) {
            // Notes from backend are already in reverse order (latest first) initially
            // Only reverse when they come from saveNote response (chronological order)
            this.displayedNotes = this.notes.slice(0, this.maxNotes);
        }
    }

    async loadNotes(): Promise<void> {
        if (!this.userId) return;

        this.isLoading = true;
        try {
            const { data, error } = await this.userService.getUserDetails(this.userId);
            if (!error && data && data.notes) {
                this.notes = data.notes;
                this.updateDisplayedNotes();
            }
        } catch (err) {
            // Handle error silently or show notification
        } finally {
            this.isLoading = false;
        }
    }

    openAddNoteModal(): void {
        this.editedNote = '';
        this.isEditingNote = false;
        this.editingNoteId = null;
        this.isClosingNoteModal = false;
        this.showNoteModal = true;
        this.showNoteError = false;
    }

    openEditNoteModal(note: any): void {
        this.editedNote = note.content || '';
        this.isEditingNote = true;
        this.editingNoteId = note.id;
        this.isClosingNoteModal = false;
        this.showNoteModal = true;
        this.showNoteError = false;
    }

    closeNoteModal(): void {
        this.isClosingNoteModal = true;
        setTimeout(() => {
            this.showNoteModal = false;
            this.isClosingNoteModal = false;
            this.editedNote = '';
            this.isEditingNote = false;
            this.editingNoteId = null;
        }, 180);
    }

    confirmNote(noteForm?: any): void {
        if (noteForm && noteForm.invalid) {
            this.showNoteError = true;
            return;
        }

        if (!this.editedNote?.trim()) {
            this.showNoteError = true;
            return;
        }
        
        this.showNoteError = false;

        if (!this.userId) return;

        this.userService.saveNote(this.userId, 'App\\Models\\User', this.editedNote, this.editingNoteId).then((response) => {
            if (!response.error) {
                this.notifications.success('Success', this.isEditingNote ? 'Note updated successfully.' : 'Note added successfully.');
                // Response can be either a note list or a single note.
                if (Array.isArray(response.data)) {
                    this.notes = [...response.data].reverse();
                    this.updateDisplayedNotes();
                } else if (response.data && typeof response.data === 'object') {
                    const updatedNote = response.data as any;
                    const existingNotes = this.notes || [];
                    const noteExists = existingNotes.some(n => n.id === updatedNote.id);
                    this.notes = noteExists
                        ? existingNotes.map(n => (n.id === updatedNote.id ? updatedNote : n))
                        : [updatedNote, ...existingNotes];
                    this.updateDisplayedNotes();
                } else {
                    this.loadNotes();
                }
                this.closeNoteModal();
            }
        });
    }

    async deleteNote(note: any): Promise<void> {
        const isConfirmed = await this.interaction.confirm({
            title: 'Delete note?',
            message: 'Are you sure you want to delete this note? This action is permanent and cannot be undone.',
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Delete message',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });

        if (!isConfirmed) {
            return;
        }

        this.userService.deleteNote(note.id).then((response) => {
            if (!response.error) {
                this.notifications.success('Success', 'Note deleted successfully.');
                // Remove note from list
                if (this.notes) {
                    this.notes = this.notes.filter(n => n.id !== note.id);
                    this.updateDisplayedNotes();
                } else {
                    // If notes not loaded, reload them
                    this.loadNotes();
                }
            }
        });
    }

    constructor(
        private userService: UserService,
        private notifications: NotificationsService,
        private interaction: InteractionService
    ) {}
}

